'use strict';

import Config from './config';
import Store from './store';
import Log from './log';
import MQTT from 'mqtt';

export default class DomoticalabsApi {
    constructor(clientId, clientSecret, callbackTokenNotValid) {
        let _this = this;

        this.__setInternalStructures();
        // Init clientId and clientSecret
        this.__setClientId(clientId, clientSecret);
        // Init status service
        this.info().__callStatusApi();
        // Init status debug log
        this.debug(this.__getOptionConfig('debug'));
        // Init MQTT services
        this.__mqttConnect();
        // Init function callback for token not validable
        this._callbackTokenNotValid = callbackTokenNotValid;
    }

    /**
     * @method auth - Contain all methods for authentication
     * @type {Object}
     *
     * @property {function} object.signIn - Login with user/pass
     * @property {function} object.signOut - Destroy logged session
     * @property {function} object.currentUser - Return user data
     * @property {function} object.isLogged - Return true/false if logged session is active
     * @property {function} object.createUserWithEmail - Register new user with email and password (request confirm via email)
     * @property {function} object.createUserIoT - Register new user with username and password (no confirm email is request)
     * @property {function} object.confirmUserEmail - Execute challenge on user
     * @property {function} object.removeUser - Remove logged user
     * @property {function} object.resetPassword - Sent an email with the instructions to reset the password
     * @property {function} object.setPassword - Set a new password
     */
    auth() {
        let _this = this;

        return {

            /**
             * @method login - Login with user/pass
             *
             * @param {Object}	params - An Object with username, password, remember_me, type ('IoT' or 'standard') not mandatory
             */
            signIn: function(params = {
                'username': '',
                'password': '',
                'remember_me': false,
                'type': 'standard'
            }) {
                Log.print('Call auth().signIn()');
                let promise = new Promise(function(resolve, reject) {
                    _this.__setUserPass(params.username, params.password, params.type);
                    _this.__apiCallAjax({
                        url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + _this.__getAuthUser() + '/tokens',
                        method: 'POST',
                        data: {
                            'password': _this.__getAuthPassword(),
                            'client_id': _this.__getClientId(),
                            'client_secret': _this.__getClientSecret(),
                            'grant_type': 'password'
                        }
                    })
                    .then((data) => {
                        if (params.remember_me === true) {
                            _this.__setOptionConfig('save_local_storage', true);
                        }
                        _this.__setTokenData(data);
                        _this.__mqttConnect();
                        resolve(data);
                    })
                    .catch((error) => {
                        Log.error(error);
                        reject(error);
                    });
                });

                return promise;
            },

            /**
             * @method signOut - Destroy logged session
             */
            signOut: function() {
                Log.print('Call auth().signOut()()');
                _this.__mqttDisconnect();
                _this.__setInternalStructures();
                _this.__setOptionConfig('user_email', null);
                _this.__setOptionConfig('user_secret', null);
                _this.__setOptionConfig('user_access_token', null);
                _this.__setOptionConfig('user_refresh_token', null);
                _this.__setOptionConfig('user_expiration_token', null);
                Store.destroy();
            },

            /**
             * @method user - Return user data
             */
            currentUser: function() {
                Log.print('Call auth().currentUser()');
                let promise = new Promise(function(resolve, reject) {
                    _this.__apiCall({
                        url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + _this.__getAuthUser() + '/describe',
                        method: 'GET',
                        headers: {
                            authorization: 'Bearer ' + _this.__getToken()
                        }
                    })
                    .then((data) => {
                        Log.print(data);
                        resolve(data);
                    })
                    .catch((error) => {
                        Log.error(error);
                        reject(error);
                    });
                });

                return promise;
            },

            /**
             * @method isLogged - Return true/false if logged session is active
             */
            isLogged: function() {
                Log.print('Call auth().isLogged()');
                if (_this.__tokenExists()) {
                    return true;
                }
                return false;
            },

            /**
             * @method registerUser - Register new user with email and password (request confirm via email)
             *
             * @param {Object}	params - An Object with email, password, name, surname
             */
            createUserWithEmail: function(params = {
                email: '',
                password: '',
                name: '',
                surname: ''
            }) {
                Log.print('Call auth().createUserWithEmail()');
                let promise = new Promise(function(resolve, reject) {
                    _this.auth().__register(params, 1)
                    .then((data) => {
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
                });

                return promise;
            },

            /**
             * @method registerIoT - Register new user with username and password (no confirm email is request)
             *
             * @param {Object}	params - An Object with username, password, name, surname
             */
            createUserIoT: function(params = {
                username: '',
                password: '',
                name: '',
                surname: ''
            }) {
                Log.print('Call auth().registerIoT()');
                let promise = new Promise(function(resolve, reject) {
                    _this.auth().__register(params, 2)
                    .then((data) => {
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
                });

                return promise;
            },

            /**
             * @method confirmUserEmail - Execute challenge on user
             *
             * @param {String} email User identity (eg: email)
             * @param {String} cid ChallengeId received via email
             */
            confirmUserEmail: function(email, cid) {
                Log.print('Call things().confirmUserEmail()');
                let promise = new Promise(function(resolve, reject) {
                    _this.__apiCallAjax({
                        url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + email + '/challenge',
                        method: 'POST',
                        data: {
                            challenge: cid
                        }
                    })
                    .then((data) => {
                        Log.print(data);
                        resolve(data);
                    })
                    .catch((error) => {
                        Log.error(error);
                        reject(error);
                    });
                });

                return promise;
            },

            /**
             * @method removeUser - Remove logged user
             */
            removeUser: function() {
                Log.print('Call auth().removeUser()');
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + _this.__getAuthUser() + '/describe',
                            method: 'DELETE',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            }
                        })
                        .then((data) => {
                            Log.print(data);
                            _this.logout();
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject();
                    }
                });

                return promise;
            },

            /**
             * @method updateUser - Updatee logged user data
             */
            updateUser: function(params = {
                password: '',
                name: '',
                surname: '',
                host: ''
            }) {
                Log.print('Call auth().removeUser()');
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        let dataUpdate = {};

                        if (params.name !== undefined) dataUpdate['name'] = params.name;
                        if (params.surname !== undefined) dataUpdate['surname'] = params.surname;
                        if (params.password !== undefined) dataUpdate['password'] = params.password;
                        if (params.host !== undefined) dataUpdate['host'] = params.host;

                        _this.__apiCall({
                            url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + _this.__getAuthUser() + '/describe',
                            method: 'PUT',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: dataUpdate
                        })
                        .then((data) => {
                            Log.print(data);
                            _this.logout();
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject();
                    }
                });

                return promise;
            },

            /**
             * @method resetPassword - Sent an email with the instructions to reset the password
             *
             * @param {String} user User identity (eg: email)
             */
            resetPassword(username) {
                Log.print('Call auth().resetPassword()');
                if (username === undefined) {
                    username = _this.__getAuthUser();
                }
                let promise = new Promise(function(resolve, reject) {
                    // if (_this.auth().isLogged()) {
                    _this.__apiCallAjax({
                        url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + username + '/reset',
                        method: 'GET'
                    })
                    .then((data) => {
                        Log.print(data);
                        resolve(data);
                    })
                    .catch((error) => {
                        Log.error(error);
                        reject(error);
                    });
                });

                return promise;
            },

            /**
             * @method setPassword - Updatee logged user data
             */
            setPassword: function(username, password, token) {
                Log.print('Call auth().setPassword()');
                let promise = new Promise(function(resolve, reject) {
                    _this.__apiCallAjax({
                        url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + username + '/reset',
                        method: 'POST',
                        headers: {
                            authorization: 'Bearer ' + _this.__getToken()
                        },
                        data: {
                            password: password,
                            challenge: token
                        }
                    })
                    .then((data) => {
                        Log.print(data);
                        resolve(data);
                    })
                    .catch((error) => {
                        Log.error(error);
                        reject(error);
                    });
                });

                return promise;
            },

            __register: function(params, type = 1) {
                Log.print('Call auth().__register()');
                let promise = new Promise(function(resolve, reject) {
                    _this.__apiCallAjax({
                        url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + (type === 1 ? params.email : params.username) + '/describe',
                        method: 'POST',
                        data: {
                            'password': params.password,
                            'name': params.name,
                            'surname': params.surname,
                            'type': type
                        }
                    })
                    .then((data) => {
                        Log.print(data);
                        resolve(data);
                    })
                    .catch((error) => {
                        Log.error(error);
                        reject(error);
                    });
                });

                return promise;
            }
        };
    }

    /* -------------------------------------------------------------------------
     *
     * Methods for manage the things
     *
     * -----------------------------------------------------------------------*/

    /**
     * @method things - Contain all methods for things
     * @type {Object}
     *
     * @property {function} object.set - Register new object
     * @property {function} object.get - Return a specific uuid object
     * @property {function} object.list - Return all user's objects
     * @property {function} object.challenge - Challenge specific uuid object
     * @property {function} object.remove - Remove a specific uuid object
     * @property {function} object.on - Subscribe to specific uuid MQTT channel
     * @property {function} object.off - Unsubscribe to specific uuid MQTT channel
     * @property {function} object.identity - Require uuid of a device with params={ device: string, mac_addr: string, esp_serial: string, stm_serial: string }
     *
     * @type {Object} object.props - Contain all thing properties
     * @property {function} object.set - Set value for a specific property
     * @property {function} object.get - Get value for a specific property
     * @property {function} object.list - Return thing properties
     * @property {function} object.tracks - Return tracks log for specific property
     *
     * @type {Object} object.schedule - Contain all schedulation properties
     * @property {function} object.set - Set schedule for a specific property
     * @property {function} object.get - Get schedule for a specific property
     * @property {function} object.update - Update schedule for specific property
     * @property {function} object.remove - Remove schedule for specific property
     */
    things(uuid) {
        let _this = this;

        return {
            uuid: uuid,
            set: function(params) {
                Log.print('Call things().set()');
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/describe',
                            method: 'POST',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: params
                        })
                        .then((data) => {
                            Log.print(data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            update: function(params = {
                label: '',
                lat: null,
                lon: null
            }) {
                Log.print('Call things().udpate()');
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + uuid + '/describe',
                            method: 'PUT',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: params
                        })
                        .then((data) => {
                            Log.print(data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            get: function(uuid) {
                Log.print('Call things().get()');
                if (uuid === undefined) {
                    uuid = this.uuid;
                }
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + uuid + '/describe',
                            method: 'GET',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            }
                        })
                        .then((data) => {
                            Log.print(data);
                            _this._things.data[data.uuid] = data;
                            _this._things.properties[data.uuid] = data['properties'];
                            Store.things(_this._things.data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            list: function(params = {
                offset: 0,
                limit: 50
            }) {
                Log.print('Call things().list()');
                let __this = this;
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/user/' + _this.__getAuthUser() + '/things',
                            method: 'POST',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: {
                                offset: params.offset,
                                limit: params.limit
                            }
                        })
                        .then((data) => {
                            Log.print(data);
                            _this._things.data = [];
                            _this._things.properties = [];
                            for (let key in data) {
                                _this._things.data[data[key].uuid] = data[key];
                                _this._things.properties[data[key].uuid] = data[key]['properties'];
                                Store.things(_this._things.data);
                            }
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            firmware: function(params = {
                'device': '',
                'fw_ver': '',
                'app_ver': ''
            }) {
                Log.print('Call iinfo().firmware()');
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/fw_update',
                            method: 'POST',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: params
                        })
                        .then((data) => {
                            Log.print(data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            challenge: function(cid, uuid) {
                Log.print('Call things().challenge()');
                if (uuid === undefined) {
                    uuid = this.uuid;
                }
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + uuid + '/challenge',
                            method: 'POST',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: {
                                challenge: cid
                            }
                        })
                        .then((data) => {
                            Log.print(data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            unchallenge: function(uuid) {
                Log.print('Call things().unchallenge()');
                if (uuid === undefined) {
                    uuid = this.uuid;
                }
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + uuid + '/unchallenge',
                            method: 'POST',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            }
                        })
                        .then((data) => {
                            Log.print(data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            identity: function(params) {
                Log.print('Call things().identity()');
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/uuid',
                            method: 'POST',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            },
                            data: params
                        })
                        .then((data) => {
                            Log.print(data);
                            resolve(data);
                        })
                        .catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;

            },
            remove: function(uuid) {
                Log.print('Call things().remove()');
                if (uuid === undefined) {
                    uuid = this.uuid;
                }
                let promise = new Promise(function(resolve, reject) {
                    if (_this.auth().isLogged()) {
                        _this.__apiCall({
                            url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + uuid + '/describe',
                            method: 'DELETE',
                            headers: {
                                authorization: 'Bearer ' + _this.__getToken()
                            }
                        }).then((data) => {
                            Log.print(data);
                            resolve(data);
                        }).catch((error) => {
                            Log.error(error);
                            reject(error);
                        });
                    } else {
                        reject(false);
                    }
                });

                return promise;
            },
            change() {
                let _thing = this;

                return {
                    on: function(callback) {
                        Log.print('Call things().change().on()');
                        if (uuid === undefined) {
                            uuid = this.uuid;
                        }
                        if (_this._things.properties[uuid] !== undefined) {
                            for (let i = 0; i < _this._things.properties[uuid].length; i++) {
                                Log.print(_this._things.properties[uuid][i]);
                                if (_this._things.properties[uuid][i]['readable'] === true) {
                                    let topic = 'api/v2/report/' + uuid + '/' + _this._things.properties[uuid][i]['uid'];

                                    Log.print('Subscribe to ' + topic);
                                    _this._mqtt.subscribe(topic);
                                }
                            }
                            if (callback !== undefined) _this._things.callbacks.props[uuid] = callback;
                        } else {
                            return false;
                        }
                        return true;
                    },
                    off: function() {
                        Log.print('Call things().change().off()');
                        if (uuid === undefined) {
                            uuid = this.uuid;
                        }
                        if (_this._things.properties[uuid] !== undefined) {
                            for (let i = 0; i < _this._things.properties[uuid].length; i++) {
                                let topic = 'api/v2/report/' + uuid + '/' + _this._things.properties[uuid][i]['uid'];

                                Log.print('Unsubscribe to  ' + topic);
                                _this._mqtt.unsubscribe(topic);
                            }
                            _this._things.callbacks.props['uuid'] = false;
                        } else {
                            return false;
                        }
                        return true;
                    },
                    online: function(callback) {
                        Log.print('Call things().change().online()');
                        if (uuid === undefined) {
                            uuid = this.uuid;
                        }
                        if (_this._things.properties[uuid] !== undefined) {
                            let topic = 'api/v2/online/report/' + uuid;

                            Log.print('Subscribe to' + topic);
                            _this._mqtt.subscribe(topic);
                            if (callback !== undefined) _this._things.callbacks.online[uuid] = callback;
                        } else {
                            return false;
                        }
                        return true;
                    }
                };
            },
            props(pid) {
                let _thing = this;

                return {
                    pid: pid,
                    set: function(value) {
                        let topic = 'api/v2/command/' + _thing.uuid + '/' + this.pid;

                        Log.print('Set value ' + value + ' to ' + topic);
                        _this._mqtt.publish(topic, Buffer.from(value));

                        return true;
                    },
                    get: function(pid) {
                        Log.print('Call things().props().get()');
                        if (pid === undefined) {
                            pid = this.pid;
                        }
                        let promise = new Promise(function(resolve, reject) {
                            if (_this.auth().isLogged()) {
                                _this.__apiCall({
                                    url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + _thing.uuid + '/' + pid,
                                    method: 'GET',
                                    headers: {
                                        authorization: 'Bearer ' + _this.__getToken()
                                    }
                                })
                                .then((data) => {
                                    Log.print(data);
                                    resolve(data);
                                })
                                .catch((error) => {
                                    Log.error(error);
                                    reject(error);
                                });
                            } else {
                                reject(false);
                            }
                        });

                        return promise;
                    },
                    update: function(params = {
                        label: ''
                    }) {
                        Log.print('Call things().props().udpate()');
                        if (pid === undefined) {
                            pid = this.pid;
                        }
                        let promise = new Promise(function(resolve, reject) {
                            if (_this.auth().isLogged()) {
                                _this.__apiCall({
                                    url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + _thing.uuid + '/' + pid + '/describe',
                                    method: 'PUT',
                                    headers: {
                                        authorization: 'Bearer ' + _this.__getToken()
                                    },
                                    data: params
                                })
                                .then((data) => {
                                    Log.print(data);
                                    resolve(data);
                                })
                                .catch((error) => {
                                    Log.error(error);
                                    reject(error);
                                });
                            } else {
                                reject(false);
                            }
                        });

                        return promise;
                    },
                    list: function() {
                        Log.print('Call things().props().list()');
                        let promise = new Promise(function(resolve, reject) {
                            if (_this.auth().isLogged()) {
                                _this.__apiCall({
                                    url: _this.__getOptionConfig('services_server') + '/api/v2/thing/' + _thing.uuid + '/describe',
                                    method: 'GET',
                                    headers: {
                                        authorization: 'Bearer ' + _this.__getToken()
                                    }
                                })
                                .then((data) => {
                                    Log.print(data);
                                    resolve(data.properties);
                                })
                                .catch((error) => {
                                    Log.error(error);
                                    reject(error);
                                });
                            } else {
                                reject(false);
                            }
                        });

                        return promise;
                    },
                    tracks: function(params = {
                        from: '',
                        to: '',
                        limit: 0
                    }) {
                        Log.print('Call things().props().tracks()');
                        if (pid === undefined) {
                            pid = this.pid;
                        }
                        let promise = new Promise(function(resolve, reject) {
                            if (_this.auth().isLogged()) {
                                _this.__apiCall({
                                    url: _this.__getOptionConfig('services_server') + '/api/v2/tracks/' + _thing.uuid + '/' + pid,
                                    method: 'POST',
                                    headers: {
                                        authorization: 'Bearer ' + _this.__getToken()
                                    },
                                    data: {
                                        from: params.from,
                                        to: params.to,
                                        limit: params.limit
                                    }
                                })
                                .then((data) => {
                                    Log.print(data);
                                    resolve(data);
                                })
                                .catch((error) => {
                                    Log.error(error);
                                    reject(error);
                                });
                            } else {
                                reject(false);
                            }
                        });

                        return promise;
                    },
                    aggregations: function(params = {
                        from: '',
                        to: '',
                        kind: 'day',
                        limit: 0
                    }) {
                        Log.print('Call things().props().tracks()');
                        if (pid === undefined) {
                            pid = this.pid;
                        }
                        let promise = new Promise(function(resolve, reject) {
                            if (_this.auth().isLogged()) {
                                _this.__apiCall({
                                    url: _this.__getOptionConfig('services_server') + '/api/v2/aggregations/' + _thing.uuid + '/' + pid,
                                    method: 'POST',
                                    headers: {
                                        authorization: 'Bearer ' + _this.__getToken()
                                    },
                                    data: {
                                        from: params.from,
                                        to: params.to,
                                        kind: params.kind,
                                        limit: params.limit
                                    }
                                })
                                .then((data) => {
                                    Log.print(data);
                                    resolve(data);
                                })
                                .catch((error) => {
                                    Log.error(error);
                                    reject(error);
                                });
                            } else {
                                reject(false);
                            }
                        });

                        return promise;
                    },
                    schedule() {
                        let _prop = this;

                        return {
                            set: function(params = {
                                value: '',
                                when: {}
                            }) {
                                Log.print('Call things().props().schedule().set()');
                                let promise = new Promise(function(resolve, reject) {
                                    if (_this.auth().isLogged()) {
                                        _this.__apiCall({
                                            url: _this.__getOptionConfig('services_server') + '/api/v2/schedules/' + _thing.uuid + '/' + _prop.pid,
                                            method: 'POST',
                                            headers: {
                                                authorization: 'Bearer ' + _this.__getToken()
                                            },
                                            data: params
                                        })
                                        .then((data) => {
                                            Log.print(data);
                                            resolve(data);
                                        })
                                        .catch((error) => {
                                            Log.error(error);
                                            reject(error);
                                        });
                                    } else {
                                        reject(false);
                                    }
                                });

                                return promise;
                            },
                            get: function() {
                                Log.print('Call things().props().schedule().get()');
                                let promise = new Promise(function(resolve, reject) {
                                    if (_this.auth().isLogged()) {
                                        _this.__apiCall({
                                            url: _this.__getOptionConfig('services_server') + '/api/v2/schedules/' + _thing.uuid + '/' + _prop.pid,
                                            method: 'GET',
                                            headers: {
                                                authorization: 'Bearer ' + _this.__getToken()
                                            }
                                        })
                                        .then((data) => {
                                            Log.print(data);
                                            resolve(data);
                                        })
                                        .catch((error) => {
                                            Log.error(error);
                                            reject(error);
                                        });
                                    } else {
                                        reject(false);
                                    }
                                });

                                return promise;
                            },
                            update: function(params = {
                                id: '',
                                value: '',
                                when: {}
                            }) {
                                Log.print('Call things().props().schedule().update()');
                                let promise = new Promise(function(resolve, reject) {
                                    if (_this.auth().isLogged()) {
                                        _this.__apiCall({
                                            url: _this.__getOptionConfig('services_server') + '/api/v2/schedules/' + _thing.uuid + '/' + _prop.pid,
                                            method: 'PUT',
                                            headers: {
                                                authorization: 'Bearer ' + _this.__getToken()
                                            },
                                            data: params
                                        })
                                        .then((data) => {
                                            Log.print(data);
                                            resolve(data);
                                        })
                                        .catch((error) => {
                                            Log.error(error);
                                            reject(error);
                                        });
                                    } else {
                                        reject(false);
                                    }
                                });

                                return promise;
                            },
                            remove: function(params) {
                                Log.print('Call things().props().schedule().remove()');
                                let promise = new Promise(function(resolve, reject) {
                                    if (_this.auth().isLogged()) {
                                        _this.__apiCall({
                                            url: _this.__getOptionConfig('services_server') + '/api/v2/schedules/' + _thing.uuid + '/' + _prop.pid,
                                            method: 'DELETE',
                                            headers: {
                                                authorization: 'Bearer ' + _this.__getToken()
                                            },
                                            data: params
                                        })
                                        .then((data) => {
                                            Log.print(data);
                                            resolve(data);
                                        })
                                        .catch((error) => {
                                            Log.error(error);
                                            reject(error);
                                        });
                                    } else {
                                        reject(false);
                                    }
                                });

                                return promise;
                            }
                        };
                    }
                };
            }
        };
    }

    /* -------------------------------------------------------------------------
     *
     * Methods for API information
     *
     * -----------------------------------------------------------------------*/

    /**
     * @method info - Contain all methods for API informatioins
     * @type {Object}
     *
     * @property {function} object.status - Return a json with API status
     * @property {function} object.maintenance - Return true/false if API is on maintenance
     * @property {function} object.version - Return API version
     */
    info() {
        let _this = this;

        return {
            status: function() {
                return _this._status;
            },
            maintenance: function() {
                return this.status()['maintenance'];
            },
            version: function() {
                return this.status()['version'];
            },
            __callStatusApi: function() {
                _this.__apiCallAjax({
                    url: _this.__getOptionConfig('services_server') + '/api/v2/status'
                })
                .then((data) => {
                    _this._status = data;
                })
                .catch((error) => {
                    Log.error(error);
                });
            }
        };

    }

    /* -------------------------------------------------------------------------
     *
     * Methods for Debug functions
     *
     * -----------------------------------------------------------------------*/

    /**
     * @method debug - Active/deactive debug mode (log message into console)
     *
     * @param {boolean}	status - true or false for set active/deactive log
     * @return {boolean}
     */
    debug(status = true) {
        Log.print('Call debug mode: ' + status);
        if (status === true) {
            Log.active();
        } else {
            Log.disable();
        }
        return status;
    }

    /* -------------------------------------------------------------------------
     *
     * Methods for AJAX/MQTT Call
     *
     * -----------------------------------------------------------------------*/

    __mqttConnect() {
        Log.print('Call __mqttConnect()');
        if (this.auth().isLogged()) {
            Log.print('Logged into MQTT');
            let _this = this;

            this._mqtt = new MQTT(Config.mqtt_endpoint, { username: this.__getAuthUser(), password: this.__getAuthPassword() });
            this._mqtt.on('message', function(topic, payload) {
                let uri = topic.split('/');

                Log.print(topic);
                if (uri[2] === 'online') {
                    let uuid = uri[uri.length - 1];

                    _this._things.callbacks.online[uuid]({
                        uuid: uuid,
                        payload: payload
                    });
                } else {
                    let uuid = uri[uri.length - 2];
                    let pid = uri[uri.length - 1];

                    if (_this._things.data[uuid].properties !== undefined) {
                        for (let key in _this._things.data[uuid].properties) {
                            if (_this._things.data[uuid].properties[key].uid === pid) {
                                _this._things.data[uuid].properties[key].value = payload.toString();
                            }
                        }
                    }

                    Store.things(_this._things.data);
                    _this._things.callbacks.props[uuid]({
                        uuid: uuid,
                        pid: pid,
                        payload: payload
                    });
                }
            });
        } else {
            Log.error('Not logged into MQTT');
        }
    }

    __mqttDisconnect() {
        this._mqtt.end();
    }

    __apiCall(params) {
        let _this = this;

        let promise = new Promise(function(resolve, reject) {
            if (_this.__tokenIsValid()) {
                _this.__apiCallAjax(params)
                .then((data) => {
                    resolve(data);
                })
                .catch((error) => {
                    resolve(error);
                });
            } else {
                _this.__tokenRefresh()
                .then(() => {
                    _this.__apiCallAjax(_this.__tokenRefreshHeader(params)).then((data) => {
                        resolve(data);
                    }).catch((error) => {
                        resolve(error);
                    });
                })
                .catch(() => {
                    Log.error('__apiCall(): Token not refreshed');
                });
            }
        });

        return promise;
    }

    __apiCallAjax(params) {
        Log.print('Call __apiCall(): ' + JSON.stringify(params));
        let _this = this;

        let promise = new Promise(function(resolve, reject) {
            let xhr = new XMLHttpRequest();

            xhr.timeout = 10000;
            if (params['method'] === undefined) params['method'] = 'GET';
            if (params['data'] === undefined) params['data'] = {};
            xhr.open(params['method'], params['url']);
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('Content-Type', 'application/json');
            if (params['headers'] !== undefined) {
                for (let key in params['headers']) {
                    xhr.setRequestHeader(key, params.headers[key]);
                }
            }
            xhr.onload = function() {
                if (xhr.status === 200) {
                    Log.print(xhr.responseText);
                    resolve(JSON.parse(xhr.responseText));
                } else {
                    Log.error(xhr.responseText);
                    reject(JSON.parse(xhr.responseText));
                }
            };
            xhr.ontimeout = function(e) {
                Log.error(e);
                reject(JSON.parse(xhr.responseText));
            };
            xhr.send(JSON.stringify(params['data']));
        });

        return promise;
    }

    __ajaxParamsEncode(object) {
        let encodedString = '';

        for (let prop in object) {
            if (object.hasOwnProperty(prop)) {
                if (encodedString.length > 0) {
                    encodedString += '&';
                }
                encodedString += encodeURI(prop + '=' + object[prop]);
            }
        }
        return encodedString;
    }

    /* -------------------------------------------------------------------------
     *
     * Methods for Token management
     *
     * -----------------------------------------------------------------------*/

    __tokenIsValid() {
        Log.print('Call __tokenIsValid()');
        if (this.__tokenExists()) {
            if (!this.__tokenIsExpired()) {
                return true;
            }
        }
        return false;
    }

    __tokenIsExpired() {
        Log.print('Call __tokenIsExpired()');
        let dateNow = parseInt(new Date().getTime() / 1000, 0);

        if (dateNow > this.__getOptionConfig('user_expiration_token')) {
            Log.warn('__tokenIsExpired(): expired');
            return true;
        }
        Log.print('__tokenIsExpired(): valid');
        return false;
    }

    __tokenExists() {
        Log.print('Call __tokenExists()');
        if (this.__getOptionConfig('user_access_token') !== null) {
            if (this.__tokenRefreshExists()) {
                return true;
            }
        }
        return false;
    }

    __tokenRefreshExists() {
        Log.print('Call __tokenRefreshExists()');
        if (this.__getOptionConfig('user_refresh_token') !== null) {
            return true;
        }
        return false;
    }

    __tokenRefresh() {
        Log.print('Call __tokenRefresh()');
        let _this = this;

        let promise = new Promise(function(resolve, reject) {
            let data = {
                'refresh_token': _this.__getRefreshToken(),
                'client_id': _this.__getClientId(),
                'client_secret': _this.__getClientSecret(),
                'grant_type': 'refresh_token'
            };

            _this.__apiCallAjax({
                url: _this.__getOptionConfig('authentication_server') + '/api/v2/user/' + _this.__getAuthUser() + '/refresh',
                method: 'POST',
                data: data
            })
            .then((data) => {
                Log.print('__tokenRefresh(): Token refreshed');
                Log.print(data);
                _this.__setTokenData(data);
                resolve(data);
            })
            .catch((error) => {
                Log.error('__tokenRefresh(): Token not refreshed');
                Log.error(error);
                if (_this._callbackTokenNotValid !== undefined) {
                    _this._callbackTokenNotValid();
                }
                reject(error);
            });
        });

        return promise;
    }

    __tokenRefreshHeader(params) {
        Log.print('Call __tokenRefreshHeader()');
        if (params['headers'] !== undefined) {
            if (params['headers']['authorization'] !== undefined) {
                params['headers']['authorization'] = 'Bearer ' + this.__getToken();
            }
        }
        return params;
    }

    /* -------------------------------------------------------------------------
     *
     * Methods for get/set options _config Object
     *
     * -----------------------------------------------------------------------*/

    __getAuthUser() {
        return this.__getOptionConfig('user_email');
    }

    __setInternalStructures() {
        this._config = (Store.exists() ? Store.getData() : Config);
        this._status = {};
        this._things = {
            data: {},
            properties: {},
            callbacks: {
                props: {},
                online: {}
            }
        };
    }

    __setClientId(clientId, clientSecret) {
        Log.print('Call __setClientId()');
        this.__setOptionConfig('user_client_id', clientId);
        this.__setOptionConfig('user_client_secret', clientSecret);
    }

    __setUserPass(username, password, type = 'standard') {
        Log.print('Call __setUserPass()');
        this.__setOptionConfig('authentication_type', (type === 'IoT' ? 'IoT' : 'standard'));
        this.__setOptionConfig('user_email', username);
        this.__setOptionConfig('user_secret', password);
    }

    __setTokenData(data) {
        this.__setOptionConfig('user_access_token', data.access_token);
        if (data.refresh_token !== undefined) this.__setOptionConfig('user_refresh_token', data.refresh_token);
        this.__setOptionConfig('user_expiration_token', parseInt(data.access_token_expires, 0));
        if (data.refresh_token_expires !== undefined) this.__setOptionConfig('user_expiration_refresh_token', parseInt(data.refresh_token_expires, 0));
    }

    __getToken() {
        return this.__getOptionConfig('user_access_token');
    }

    __getAuthType() {
        return this.__getOptionConfig('authentication_type');
    }

    __getClientId() {
        return this.__getOptionConfig('user_client_id');
    }

    __getClientSecret() {
        return this.__getOptionConfig('user_client_secret');
    }

    __getRefreshToken() {
        return this.__getOptionConfig('user_refresh_token');
    }

    __getAuthPassword() {
        return this.__getOptionConfig('user_secret');
    }

    __getConfig() {
        return this._config;
    }

    __setOptionConfig(option, value) {
        Log.print('Call __setOptionConfig(): ' + option + ' => ' + value);
        if (this.__getOptionConfig('save_local_storage')) {
            Store.save(this.__getConfig());
        }
        this._config[option] = value;
    }

    __getOptionConfig(option) {
        return this._config[option];
    }

}



// WEBPACK FOOTER //
// ./src/index.js